import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import i18n from "../../i18n/i18n";
const BASE_URL = process.env.REACT_APP_BASE_URL;

// all driver documents
export const fetchDriverDocuments = createAsyncThunk(
  "driver_documents/fetchAll",
  async ({ page = 1, status = "", document_number = "",
    is_auto_renewal = "",
    has_expire_date = "" }, { rejectWithValue }) => {
    try {
      const normalize = (val) => {
        if (val === "true" || val === true) return 1;
        if (val === "false" || val === false) return 0;
        return "";
      };
      const response = await axios.get(`${BASE_URL}/driver-documents?page=${page}&status=${status}&document_number=${document_number}&is_auto_renewal=${normalize(is_auto_renewal)}&has_expire_date=${normalize(has_expire_date)}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
          Lang: i18n.language,
        },
      });
      return response.data; // Return the whole response including pagination
    } catch (error) {
      const message = error.response?.data?.message || "Failed load data";
      return rejectWithValue(message);
    }
  }
);

// add driver documents
export const addDriverDocuments = createAsyncThunk(
  "driver_documents/add",
  async (userData, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${BASE_URL}/driver-documents`, userData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "multipart/form-data",
          Lang: i18n.language,
        },
      });
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "An unknown error occurred";
      return rejectWithValue(message);
    }
  }
);
// add driver documents comments
export const addDriverDocumentsComments = createAsyncThunk(
  "driver_documents/comments",
  async ({ comment, id }, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${BASE_URL}/driver-documents/${id}/comments`, comment, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
          Lang: i18n.language,
        },
      });
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "An unknown error occurred";
      return rejectWithValue(message);
    }
  }
);
// get driver documents record
export const driverDocumentRecord = createAsyncThunk(
  "driver_documents/driverDocumentRecord",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${BASE_URL}/driver-documents/${id}`);
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "An unknown error occurred";
      return rejectWithValue({ message });
    }
  }
);
// Fetch all driver documents once (for filters)
export const fetchAllDriverDocuments = createAsyncThunk(
  "driver_documents/fetchAllFull",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${BASE_URL}/driver-documents`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
          Lang: i18n.language,
        },
      });
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to load all data";
      return rejectWithValue(message);
    }
  }
);
const DriverDocumentsSlice = createSlice({
  name: "driver_documents",
  initialState: {
    driver_documents: [],
    all_driver_documents: [],
    record: null,
    isLoading: false,
    error: null,
    success: null,
    successComment: null,
    errorComment: null,
    successDelete: null,
    errorDelete: null,
    pagination: null,
  },
  reducers: {
    clearState: (state) => {
      state.error = null;
      state.success = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDriverDocuments.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchDriverDocuments.fulfilled, (state, action) => {
        state.isLoading = false;
        state.driver_documents = action.payload.data.data;
        state.pagination = action.payload.data.pagination;
      })
      .addCase(fetchDriverDocuments.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to load data";
      })
      .addCase(fetchAllDriverDocuments.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAllDriverDocuments.fulfilled, (state, action) => {
        state.isLoading = false;
        state.all_driver_documents = action.payload.data.data;
      })
      .addCase(fetchAllDriverDocuments.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to load all driver document";
      })
      .addCase(addDriverDocuments.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(addDriverDocuments.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload.message;
      })
      .addCase(addDriverDocuments.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to add data";
      })
      .addCase(addDriverDocumentsComments.pending, (state) => {
        state.isLoading = true;
        state.errorComment = null;
      })
      .addCase(addDriverDocumentsComments.fulfilled, (state, action) => {
        state.isLoading = false;
        state.successComment = action.payload.message;
      })
      .addCase(addDriverDocumentsComments.rejected, (state, action) => {
        state.isLoading = false;
        state.errorComment = action.payload || "Failed to add data";
      })
      .addCase(driverDocumentRecord.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(driverDocumentRecord.fulfilled, (state, action) => {
        state.isLoading = false;
        state.record = action.payload.data;
      })
      .addCase(driverDocumentRecord.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error?.message || "Failed to add data";
      });
  },
});

export const { clearState } = DriverDocumentsSlice.actions;
export default DriverDocumentsSlice.reducer;